/**
 * Manpower P&L — Cost dashboards react-query hooks (READ-ONLY).
 *
 * Each hook is enabled-gated on the params it needs, so a query only fires once the
 * user has picked a city/facility and a date/month. Returns the typed `data` payload
 * (already unwrapped from the envelope) so components stay thin.
 */
import { useQuery } from '@tanstack/react-query';
import {
	ManpowerCostApiService,
	CityDailyCost,
	CityMonthlyCost,
	FacilityDailyCostRow,
	WeeklyCostBuckets,
} from '../../../services/manpowerCostApi';

const COMMON = {
	staleTime: 60 * 1000,
	refetchOnWindowFocus: false,
	retry: 1,
} as const;

/**
 * City daily cost. Normalises the envelope's `data` (object OR array) down to a single
 * CityDailyCost or null. Gated on a non-empty date.
 */
export const useCityDailyCost = (cityId: number | '', date: string) => {
	return useQuery({
		queryKey: ['manpower', 'cost', 'city-daily', cityId, date],
		enabled: !!date,
		queryFn: async (): Promise<CityDailyCost | null> => {
			const res = await ManpowerCostApiService.getCityDaily({
				cityId: cityId === '' ? undefined : cityId,
				date,
			});
			const payload = res?.data;
			if (Array.isArray(payload)) return payload[0] ?? null;
			return payload ?? null;
		},
		...COMMON,
	});
};

/** City monthly cost (week1-4 + est). Gated on a non-empty month. */
export const useCityMonthlyCost = (cityId: number | '', month: string) => {
	return useQuery({
		queryKey: ['manpower', 'cost', 'city-monthly', cityId, month],
		enabled: !!month,
		queryFn: async (): Promise<CityMonthlyCost | null> => {
			const res = await ManpowerCostApiService.getCityMonthly({
				cityId: cityId === '' ? undefined : cityId,
				month,
			});
			return res?.data ?? null;
		},
		...COMMON,
	});
};

/** Facility daily cost by vendor. Gated on a facility AND a date. */
export const useFacilityDailyCost = (facilityId: number | '', date: string) => {
	return useQuery({
		queryKey: ['manpower', 'cost', 'facility-daily', facilityId, date],
		enabled: facilityId !== '' && !!date,
		queryFn: async (): Promise<FacilityDailyCostRow[]> => {
			const res = await ManpowerCostApiService.getFacilityDaily({
				facilityId: facilityId as number,
				date,
			});
			return res?.data ?? [];
		},
		...COMMON,
	});
};

/** Facility weekly buckets for a month. Gated on a facility AND a month. */
export const useFacilityWeeklyCost = (facilityId: number | '', month: string) => {
	return useQuery({
		queryKey: ['manpower', 'cost', 'facility-weekly', facilityId, month],
		enabled: facilityId !== '' && !!month,
		queryFn: async (): Promise<WeeklyCostBuckets | null> => {
			const res = await ManpowerCostApiService.getFacilityWeekly({
				facilityId: facilityId as number,
				month,
			});
			return res?.data ?? null;
		},
		...COMMON,
	});
};
